/**
 * Chart probe bridge — a tiny module-level handle that lets non-map code (the
 * Route sheet's "Check route" button) run geometry against the live chart the
 * map has loaded, without reaching into MapView's internals or React refs.
 *
 * MapView registers a probe once the chart index exists and re-registers it as
 * cells load; anything else calls getChartProbe() to reuse the SAME land source
 * (LNDARE polygons from the chart spatial index) and the SAME charted-depth
 * sampler the grounding alarm uses. Null until a chart is loaded, so callers can
 * tell the user "charts still loading" rather than getting a false all-clear.
 */
import type { LngLat } from '@/routing';

export interface ChartProbe {
  /** Outer rings of charted land (LNDARE) overlapping a leg, [lon, lat] vertices. */
  landRingsForLeg: (from: LngLat, to: LngLat) => LngLat[][];
  /** Charted depth under a point (m, chart datum), or null when there's no data. */
  depthAt: (pos: LngLat) => number | null;
  /** Whether a point falls inside the chart coverage currently loaded. */
  inCoverage: (pos: LngLat) => boolean;
  /**
   * Height of tide above chart datum at a point and time, metres, or null when
   * there's no tide-height model covering that spot. Reads the nearest training
   * port's curve, so the route check can add the tide on top of the charted depth
   * to work out a tidal gate. Null (not zero) where we genuinely can't say, so the
   * check flags "needs a manual tide calc" rather than inventing a height.
   */
  tideHeightAt: (pos: LngLat, ms: number) => number | null;
}

let current: ChartProbe | null = null;

/** MapView calls this to publish (or, with null, retract) the live chart probe. */
export function setChartProbe(probe: ChartProbe | null): void {
  current = probe;
}

/** The live chart probe, or null when no chart is loaded yet. */
export function getChartProbe(): ChartProbe | null {
  return current;
}
